import { useCallback, useMemo, useRef } from 'react';
import type { Layer, PathOptions } from 'leaflet';
import type * as GeoJSONTypes from 'geojson';

import {
  buildStandLayerKey,
  coreStandStyle,
  featureStandId,
  NEUTRAL_FOREST,
  strategyBaseColor,
} from './FmStandMap.helpers';
import type { FmStandMapProps, StandStyleContext } from './FmStandMap.types';
import { useMeasuredHeight } from '@/components/Map/useMeasuredHeight';
import { usePropertyForestSnapshot } from '@/contexts/usePropertyForestSnapshot';
import { FM_MAP_STAGE_SIZE_PX } from '@/shared/ui/mapStageSizes';
import { markMapLayerElement } from '@/components/ui/mapLayerDomMarkers';

function resolveMapData(
  props: FmStandMapProps,
  routeSnapshot: ReturnType<typeof usePropertyForestSnapshot>,
) {
  return {
    geojson:
      props.geojson !== undefined ? props.geojson : (routeSnapshot.context?.standsGeoJson ?? null),
    bbox: props.bbox !== undefined ? props.bbox : (routeSnapshot.context?.bbox ?? null),
    usesRouteSnapshot: props.geojson === undefined && props.bbox === undefined,
  };
}

function hasUsableMapContent(
  geojson: GeoJSONTypes.FeatureCollection | null,
  mapChildren: FmStandMapProps['mapChildren'],
) {
  return Boolean((geojson && geojson.features.length > 0) || typeof mapChildren === 'function');
}

function useStandMapCallbacks(props: FmStandMapProps) {
  const {
    interactionMode,
    selectedStandIds,
    focusedStandId,
    affectedStandIds,
    selectableStandIds,
    coloringStrategy,
    styleOverride,
    onStandClick,
    onStandHover,
    onEachStand,
  } = props;
  const clickEnabled = interactionMode?.click ?? true;
  const hoverEnabled = interactionMode?.hover ?? true;
  const buildContext = useCallback(
    (feature: GeoJSONTypes.Feature): StandStyleContext => {
      const id = featureStandId(feature);
      const featureProps = (feature.properties ?? {}) as Record<string, unknown>;
      return {
        standId: id,
        selected: selectedStandIds?.has(id) ?? false,
        focused: focusedStandId != null && focusedStandId === id,
        affected: affectedStandIds?.has(id) ?? false,
        selectable: selectableStandIds?.has(id) ?? true,
        strategyColor: strategyBaseColor(coloringStrategy, featureProps),
      };
    },
    [affectedStandIds, coloringStrategy, focusedStandId, selectableStandIds, selectedStandIds],
  );
  const layerStyle = useCallback(
    (feature?: GeoJSONTypes.Feature): PathOptions => {
      if (!feature) {
        return {
          color: NEUTRAL_FOREST,
          fillColor: NEUTRAL_FOREST,
          weight: 2,
          fillOpacity: 0.28,
        };
      }
      const context = buildContext(feature);
      const derived = coreStandStyle({
        strategy: coloringStrategy,
        selected: context.selected,
        focused: context.focused,
        affected: context.affected,
        selectable: context.selectable,
        baseColor: context.strategyColor,
        props: (feature.properties ?? {}) as Record<string, unknown>,
      });
      const override = styleOverride?.(feature, context);
      if (!override) return derived;
      // Both classes apply: the selection halo and, say, a reveal wave.
      const className = [derived.className, override.className].filter(Boolean).join(' ');
      return { ...derived, ...override, className: className || undefined };
    },
    [buildContext, coloringStrategy, styleOverride],
  );
  const onEachFeature = useCallback(
    (feature: GeoJSONTypes.Feature, layer: Layer) => {
      const context = buildContext(feature);
      markMapLayerElement(layer, {
        layerId: 'forest-stands',
        featureId: context.standId,
        selected: context.selected,
      });
      if (!context.selectable) return;
      if (clickEnabled && onStandClick) {
        layer.on('click', () => onStandClick(context.standId));
      }
      if (hoverEnabled && onStandHover) {
        layer.on('mouseover', () => onStandHover(context.standId));
        layer.on('mouseout', () => onStandHover(null));
      }
      onEachStand?.(feature, layer, context);
    },
    [buildContext, clickEnabled, hoverEnabled, onEachStand, onStandClick, onStandHover],
  );
  return { layerStyle, onEachFeature };
}

export function useFmStandMapModel(props: FmStandMapProps) {
  const routeSnapshot = usePropertyForestSnapshot(props.propertyId ?? '');
  const { geojson, bbox, usesRouteSnapshot } = resolveMapData(props, routeSnapshot);
  const effectiveHeight = props.height ?? (props.size ? FM_MAP_STAGE_SIZE_PX[props.size] : 420);
  const fillHostRef = useRef<HTMLDivElement | null>(null);
  const measuredHeight = useMeasuredHeight(fillHostRef, effectiveHeight, 200);
  const resolvedHeight = props.fill ? measuredHeight : effectiveHeight;
  const callbacks = useStandMapCallbacks(props);
  const layerKey = useMemo(
    () =>
      buildStandLayerKey({
        coloringStrategy: props.coloringStrategy,
        focusedStandId: props.focusedStandId ?? null,
        selectedStandIds: props.selectedStandIds,
        selectableStandIds: props.selectableStandIds,
        affectedStandIds: props.affectedStandIds,
        standLayerKey: props.standLayerKey,
      }),
    [
      props.affectedStandIds,
      props.coloringStrategy,
      props.focusedStandId,
      props.selectableStandIds,
      props.selectedStandIds,
      props.standLayerKey,
    ],
  );
  return {
    ...callbacks,
    geojson,
    bbox,
    loading: usesRouteSnapshot && routeSnapshot.isLoading,
    error: usesRouteSnapshot ? routeSnapshot.error : null,
    usable: hasUsableMapContent(geojson, props.mapChildren),
    fillHostRef,
    layerKey,
    resolvedHeight,
  };
}
